/** Paket langganan UMKM Indonesia — sumber kebenaran frontend */

export const PLAN_IDS = ['free', 'basic', 'premium'] as const
export type PlanId = (typeof PLAN_IDS)[number]

export const PLAN_DISPLAY: Record<PlanId, string> = {
  free: 'Gratis',
  basic: 'Basic',
  premium: 'Premium',
}

export const PLAN_PRICES: Record<PlanId, number> = {
  free: 0,
  basic: 49000,
  premium: 149000,
}

export const PRODUCT_LIMITS: Record<PlanId, number> = {
  free: 5,
  basic: 50,
  premium: Infinity,
}

export const BRAND_LIMITS: Record<PlanId, number> = {
  free: 1,
  basic: 1,
  premium: Infinity,
}

export const PLAN_FEATURES: Record<PlanId, string[]> = {
  free: [
    '1 katalog usaha',
    'Maksimal 5 produk',
    'Tema katalog default',
    'Link katalog online',
    'Tombol WhatsApp',
    'Logo usaha',
    'Foto produk',
    'Profil usaha',
  ],
  basic: [
    'Seluruh fitur Gratis',
    'Maksimal 50 produk',
    'Kustomisasi warna katalog',
    'Artikel usaha',
    'SEO dasar katalog',
    'Badge UMKM Terverifikasi',
    'Prioritas sitemap',
    'Prioritas indexing',
  ],
  premium: [
    'Seluruh fitur Basic',
    'Katalog usaha tanpa batas',
    'Produk tanpa batas',
    'Subdomain sendiri (namatoko.umkmindonesia.my.id)',
    'Statistik pengunjung',
    'Banner promosi katalog',
    'Prioritas tampil di homepage',
    'Prioritas pencarian internal',
    'Badge Premium',
    'Halaman SEO per produk',
    'Produk masuk sitemap Google',
  ],
}

export const PLAN_DESCRIPTIONS: Record<PlanId, string> = {
  free: 'Cocok untuk mencoba platform.',
  basic: 'Cocok untuk UMKM yang mulai serius mengembangkan usaha.',
  premium: 'Solusi lengkap untuk UMKM yang ingin tampil lebih profesional.',
}

export const LEGACY_PLAN_MAP: Record<string, PlanId> = {
  trial: 'free',
  free: 'free',
  gratis: 'free',
  basic: 'basic',
  pro: 'premium',
  premium: 'premium',
}

export function normalizePlanId(plan: string | null | undefined): PlanId {
  if (!plan) return 'free'
  const key = plan.toLowerCase().trim()
  return LEGACY_PLAN_MAP[key] ?? 'free'
}

export function getPlanDisplayName(plan: string | null | undefined): string {
  return PLAN_DISPLAY[normalizePlanId(plan)]
}

export function getPlanBadge(plan: string | null | undefined): string | null {
  const id = normalizePlanId(plan)
  if (id === 'basic') return 'UMKM Terverifikasi'
  if (id === 'premium') return 'Premium'
  return null
}

export function canUseFeature(plan: string | null | undefined, feature: string): boolean {
  const id = normalizePlanId(plan)
  switch (feature) {
    case 'articles':
      return id === 'basic' || id === 'premium'
    case 'custom_theme':
      return id === 'basic' || id === 'premium'
    case 'marketing_center':
      return id === 'basic' || id === 'premium'
    case 'subdomain':
    case 'analytics':
    case 'promo_banner':
    case 'homepage_priority':
    case 'search_priority':
    case 'product_seo':
      return id === 'premium'
    case 'seo_basic':
    case 'verified_badge':
      return id === 'basic' || id === 'premium'
    default:
      return true
  }
}

export const PLANS = PLAN_IDS.map((id) => ({
  id,
  name: PLAN_DISPLAY[id],
  price: PLAN_PRICES[id],
  period: id === 'free' ? '' : '/tahun',
  description: PLAN_DESCRIPTIONS[id],
  features: PLAN_FEATURES[id],
  popular: id === 'basic',
}))

export function getProductLimit(plan: PlanId): number {
  return PRODUCT_LIMITS[plan];
}

export function getBrandLimit(plan: PlanId): number {
  return BRAND_LIMITS[plan];
}

export function formatPlanLimit(limit: number): string {
  return Number.isFinite(limit) ? String(limit) : '∞'
}

export function isUnderPlanLimit(count: number, limit: number): boolean {
  return !Number.isFinite(limit) || count < limit
}

/** Resolve brand limit dari API subscription (null = unlimited) atau fallback plans */
export function resolveBrandLimit(
  apiLimit: number | null | undefined,
  plan: PlanId
): number {
  if (apiLimit === null) return Infinity
  if (apiLimit !== undefined) return apiLimit
  return getBrandLimit(plan)
}

export function resolveProductLimit(
  apiLimit: number | null | undefined,
  plan: PlanId
): number {
  if (apiLimit === null) return Infinity
  if (apiLimit !== undefined) return apiLimit
  return getProductLimit(plan)
}
export const SEARCH_PRIORITY: Record<PlanId, number> = {
  free: 0,
  basic: 1,
  premium: 2,
};
export function isValidPlanId(plan: string): plan is PlanId {
  return PLAN_IDS.includes(plan as PlanId);
}

export function canAccessMarketingCenter(plan: string | null | undefined): boolean {
  return canUseFeature(plan, 'marketing_center')
}
